import { useState } from "react";
import { Group, ActionIcon, Paper, Button, Tooltip, Box, Timeline, Tabs, Text, ScrollArea, Divider, TextInput, Modal, ThemeIcon, Stack } from "@mantine/core";
import { IconLayoutGrid, IconMessageCircle, IconPlus, IconSquareRoundedX, IconTrash, IconPencil } from "@tabler/icons-react";
import { translate } from '../../utils/i18n';
import { createProjectWhiteboard, deleteProjectWhiteboard, fetchProjectAllWhiteboard, fetchProjectWhiteboard, fetchWhiteboardComments, saveProjectWhiteboard } from "./store/whiteboardSlice";
import { showNotification } from "@mantine/notifications";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { WhiteboardCommentItem } from "./WhiteboardCommentItem";
import { hasPermission } from "../ui/permissions";


export default function WhiteboardDrawer({ project_id, setSelectedWhiteboardId, allWhiteboard, closeDrawer, whiteboard_id }) {
    const dispatch = useDispatch();
    const [active, setActive] = useState("grid");
    const dateTimeFormat = 'DD MMM YYYY hh:mm A';

    const [editingWhiteboardId, setEditingWhiteboardId] = useState(null);
    const [renameWhiteboardName, setRenameWhiteboardName] = useState('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deletingWhiteboardId, setDeletingWhiteboardId] = useState(null);

    const { loggedInUser, projectWhiteboardComments } = useSelector((state) => state.whiteboard.whiteboard);

    const buttons = [
        // { id: "search", icon: <IconSearch size={18} /> },
        { id: "grid", label: "Pages", icon: <IconLayoutGrid size={18} /> },
        { id: "chat", label: "Comments", icon: <IconMessageCircle size={18} /> },
        // { id: "chart", icon: <IconChartBar size={18} /> },
    ];

    const handleSwitchWhiteboard = (whiteboardId) => {
        setSelectedWhiteboardId(whiteboardId);
        dispatch(fetchProjectWhiteboard({ project_id, data: { whiteboard_id: whiteboardId, user_id: loggedInUser?.loggedUserId } }));
        dispatch(fetchWhiteboardComments({ project_id, whiteboard_id: whiteboardId }));
        closeDrawer();
    };

    const getNextWhiteboardName = () => {
        // Filter only names starting with "Default Whiteboard"
        const numbers = allWhiteboard
            .map(w => {
                const match = w.whiteboard_name.match(/Default Whiteboard (\d+)/);
                return match ? parseInt(match[1], 10) : null;
            })
            .filter(n => n !== null);

        const maxNum = numbers.length > 0 ? Math.max(...numbers) : 0;

        return `Default Whiteboard ${maxNum + 1}`;
    };


    const handleCreateWhiteboard = () => {
        const newWhiteboardData = {
            whiteboard_name: getNextWhiteboardName(),
            project_id: project_id,
            created_by: loggedInUser?.loggedUserId,
            data: []
        };

        dispatch(createProjectWhiteboard({ project_id, data: newWhiteboardData })).then((response) => {
            if (response.payload.status === 200) {
                const newWhiteboard = response.payload.data;
                setSelectedWhiteboardId(newWhiteboard.id);
                dispatch(fetchProjectAllWhiteboard(project_id));
                dispatch(fetchProjectWhiteboard({ project_id, data: { whiteboard_id: newWhiteboard.id } }));
                dispatch(fetchWhiteboardComments({ project_id, whiteboard_id: newWhiteboard.id }));
                closeDrawer();
                showNotification({
                    title: translate('Whiteboard Created'),
                    message: translate('New whiteboard created successfully'),
                    color: 'green',
                });
            } else {
                showNotification({
                    title: translate('Error'),
                    message: translate('Failed to create whiteboard'),
                    color: 'red',
                });
            }
        });
    };

    const handleRenameWhiteboard = (whiteboardId) => {
        if (!renameWhiteboardName.trim()) {
            showNotification({
                title: translate('Error'),
                message: translate('Whiteboard name cannot be empty'),
                color: 'red',
            });
            return;
        }
        const nameExists = allWhiteboard.some(
            (wb) =>
                wb.whiteboard_name.toLowerCase() === renameWhiteboardName.trim().toLowerCase() &&
                wb.id !== whiteboardId // ignore current whiteboard being renamed
        );

        if (nameExists) {
            showNotification({
                title: translate('Error'),
                message: translate('Whiteboard name already exists'),
                color: 'red',
            });
            return;
        }
        const whitebordData = {
            whiteboard_name: renameWhiteboardName.trim(),
        }

        dispatch(
            saveProjectWhiteboard({
                project_id,
                whiteboard_id: whiteboardId,
                data: whitebordData
            })
        ).then((response) => {
            if (response.payload.status === 200) {
                closeDrawer();
                showNotification({
                    title: translate('Whiteboard Renamed'),
                    message: translate('Whiteboard name updated successfully'),
                    color: 'green',
                });
                dispatch(fetchProjectAllWhiteboard(project_id));
                setSelectedWhiteboardId(whiteboardId);
                dispatch(fetchWhiteboardComments({ project_id, whiteboard_id: whiteboardId }));
                setEditingWhiteboardId(null); // Exit edit mode
            } else {
                showNotification({
                    title: translate('Error'),
                    message: translate('Failed to rename whiteboard'),
                    color: 'red',
                });
            }
        });
    };

    const handleDeleteWhiteboard = (whiteboardId) => {
        if (whiteboardId === whiteboard_id) {
            showNotification({
                title: translate('Error'),
                message: translate('Cannot delete the currently active whiteboard'),
                color: 'red',
            });
            return;
        }
        setDeletingWhiteboardId(whiteboardId);
        setDeleteModalOpen(true);
    }

    const handleConfirmDeleteWhiteboard = () => {
        dispatch(
            deleteProjectWhiteboard({
                project_id,
                whiteboard_id: deletingWhiteboardId
            })
        ).then((response) => {
            if (response.payload.status === 200) {
                setDeleteModalOpen(false);
                closeDrawer();
                showNotification({
                    title: translate('Whiteboard Deleted'),
                    message: translate('Whiteboard deleted successfully'),
                    color: 'green',
                });
            } else {
                setDeleteModalOpen(false);
                showNotification({
                    title: translate('Error'),
                    message: response.payload.message || translate('Failed to delete whiteboard'),
                    color: 'red',
                });
            }
        });
    };

    return (
        <>
            {/* <Divider size="xs" mb={2} className={'!-ml-5 w-[calc(100%+2rem)]'} /> */}

            <Tabs variant="pills" mt={10} radius="sm" defaultValue="pages" className="my-tabs"
                styles={{
                    tab: { color: '#202020', backgroundColor: '#F5F8F9' },
                }}
            >
                <Tabs.List grow>
                    <Tabs.Tab value="pages" className="font-bold">
                        {translate('Pages')}
                    </Tabs.Tab>
                    <Tabs.Tab value="comments" className="font-bold">
                        {translate('Comments')}
                    </Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel value="pages" style={{ padding: '1rem' }}>
                    <Box style={{ width: '100%' }}>
                        <Group position="apart" mb="sm" justify="flex-end">
                            {hasPermission(loggedInUser && loggedInUser.llc_permissions, ['create-edit-whiteboard-page']) && (
                                <Tooltip label="Add New Page" position="top" withArrow>
                                    <Button variant="filled"
                                        color="orange"
                                        size="sm"
                                        radius="sm"
                                        onClick={handleCreateWhiteboard}
                                        leftSection={<IconPlus size={16} />}
                                    >
                                        {translate('New Page')}
                                    </Button>
                                </Tooltip>
                            )}
                        </Group>

                        <ScrollArea className={`${appLocalizer && appLocalizer.is_admin == 1 ? 'h-[calc(100vh-232px)]' : 'h-[calc(100vh-217px)]'}`} scrollbarSize={4} offsetScrollbars scrollbars="y">
                            <Group>
                                {allWhiteboard && allWhiteboard.length > 0 ? (
                                    allWhiteboard.map((wb) => (
                                        <Paper
                                            key={wb.id}
                                            shadow="sm"
                                            p="md"
                                            radius="md"
                                            withBorder
                                            style={{
                                                width: '100%',
                                                cursor: 'pointer',
                                                backgroundColor: wb.id === whiteboard_id ? '#F5F9FB' : 'white',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                            onClick={() => handleSwitchWhiteboard(wb.id)}
                                        >
                                            {editingWhiteboardId === wb.id ? (
                                                <Group style={{ flex: 1 }}>
                                                    <TextInput
                                                        value={renameWhiteboardName}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            setRenameWhiteboardName(e.target.value);
                                                        }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Button
                                                        size="xs"
                                                        color="blue"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRenameWhiteboard(wb.id);
                                                        }}
                                                    >
                                                        Save
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        variant="default"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setEditingWhiteboardId(null);
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </Group>
                                            ) : (
                                                <>
                                                    <Text>{wb.whiteboard_name}</Text>
                                                    <Group spacing="xs">
                                                        {hasPermission(loggedInUser && loggedInUser.llc_permissions, ['create-edit-whiteboard-page']) && (
                                                            <Box
                                                                className="border border-dashed rounded-full p-2 group cursor-pointer"
                                                                style={{
                                                                    borderColor: '#4D4D4D',
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.borderColor = '#ff9800';
                                                                    e.currentTarget.querySelector('svg').style.color = '#ff9800';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.borderColor = '#4D4D4D';
                                                                    e.currentTarget.querySelector('svg').style.color = '#4D4D4D';
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingWhiteboardId(wb.id);
                                                                    setRenameWhiteboardName(wb.whiteboard_name);
                                                                }}
                                                            >
                                                                <IconPencil
                                                                    size={16}
                                                                    stroke={1.25}
                                                                    style={{
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                        {hasPermission(loggedInUser && loggedInUser.llc_permissions, ['delete-whiteboard-page']) && (
                                                            <Box
                                                                className="border border-dashed rounded-full p-2 group cursor-pointer"
                                                                style={{
                                                                    borderColor: '#4D4D4D',
                                                                    transition: 'all 0.2s ease',
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    e.currentTarget.style.borderColor = '#ff0000';
                                                                    e.currentTarget.querySelector('svg').style.color = '#ff0000';
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    e.currentTarget.style.borderColor = '#4D4D4D';
                                                                    e.currentTarget.querySelector('svg').style.color = '#4D4D4D';
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDeleteWhiteboard(wb.id);
                                                                }}
                                                            >
                                                                <IconTrash
                                                                    size={20}
                                                                    stroke={1.25}
                                                                    style={{
                                                                        transition: 'all 0.2s ease',
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Group>
                                                </>
                                            )}
                                        </Paper>
                                    ))
                                ) : (
                                    <Box>{translate('No whiteboards available.')}</Box>
                                )}
                            </Group>
                        </ScrollArea>
                    </Box>
                </Tabs.Panel>
                <Tabs.Panel value="comments" style={{ padding: '1rem' }}>
                    <ScrollArea className={`${appLocalizer && appLocalizer.is_admin == 1 ? 'h-[calc(100vh-198px)]' : 'h-[calc(100vh-181px)]'}`} mt="sm" scrollbarSize={4} offsetScrollbars>
                        <Timeline color="white" bulletSize={32} style={{ textAlign: 'left' }}>
                            {projectWhiteboardComments && projectWhiteboardComments.length > 0 ? (
                                projectWhiteboardComments.map((comment) => (
                                    <WhiteboardCommentItem
                                        key={comment.id}
                                        comment={comment}
                                        dateTimeFormat={dateTimeFormat}
                                    />
                                ))
                            ) : (
                                <Text>{translate('No comments available.')}</Text>
                            )}
                        </Timeline>
                    </ScrollArea>
                </Tabs.Panel>
            </Tabs>

            <Modal
                opened={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                title={
                    <>
                        <Group spacing="xs">
                            <ThemeIcon color="red" radius="xl" size="lg" variant="filled">
                                <IconTrash size={24} />
                            </ThemeIcon>
                            <Text size="md" weight={500}>
                                {translate('Delete Whiteboard Page')}
                            </Text>
                        </Group>
                    </>
                }
                size="md"
                centered
            >
                <Divider size="xs" my={0} className='!-ml-4 w-[calc(100%+2rem)]' />
                <Stack spacing="md" pt="md">
                    <Text size="sm" ta="center" pt={5} c="#4D4D4D">
                        {translate('Are you sure you want to delete this whiteboard page?')} <br />
                        <strong>
                            {translate('All comments on this page will also be permanently deleted.')}
                        </strong> <br />
                        {translate('This action cannot be undone.')}
                    </Text>
                    <Group mt="sm" justify="flex-end">
                        <Button variant="default" onClick={() => setDeleteModalOpen(false)}>
                            {translate('Cancel')}
                        </Button>
                        <Button color="red" onClick={handleConfirmDeleteWhiteboard}>
                            {translate('Delete')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );
}
